import React from 'react';
import { 
  Info, 
  Megaphone, 
  Target, 
  BarChart2, 
  PieChart, 
  TrendingUp, 
  AlertTriangle 
} from 'lucide-react';

/**
 * Компонент с информацией о рекламных кампаниях
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Megaphone className="w-6 h-6 text-indigo-500" />
        Эффективное планирование рекламных кампаний
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Target className="w-5 h-5 text-green-500" />
          Что такое рекламная кампания?
        </h3>
        <p>
          <strong>Рекламная кампания</strong> — это комплекс целенаправленных маркетинговых мероприятий, нацеленных на продвижение продукта, услуги или бренда через различные рекламные каналы. Успешная кампания требует тщательного планирования, аналитики и постоянной оптимизации.
        </p>

        <h3 className="flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-indigo-500" />
          Ключевые метрики рекламной кампании
        </h3>
        <ul>
          <li><strong>Целевая аудитория:</strong> Точное определение группы потенциальных клиентов</li>
          <li><strong>Охват:</strong> Количество уникальных пользователей, увидевших рекламу</li>
          <li><strong>CTR (Click-Through Rate):</strong> Процент кликов от числа показов</li>
          <li><strong>Конверсия:</strong> Доля пользователей, совершивших целевое действие</li>
          <li><strong>CPC (Cost Per Click):</strong> Стоимость одного клика</li>
          <li><strong>CPA (Cost Per Action):</strong> Стоимость одной конверсии</li>
          <li><strong>ROI (Return on Investment):</strong> Возврат инвестиций в рекламу</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <PieChart className="w-5 h-5 text-orange-500" />
          Основные каналы рекламы
        </h3>
        <ul>
          <li><strong>Поисковая реклама:</strong> Показ объявлений в результатах поиска</li>
          <li><strong>Социальные сети:</strong> Таргетированная реклама в соцсетях</li>
          <li><strong>Медийная реклама:</strong> Баннеры и графические объявления</li>
          <li><strong>Видеореклама:</strong> Рекламные ролики на YouTube и других платформах</li>
          <li><strong>Email-маркетинг:</strong> Рассылки для существующих и потенциальных клиентов</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">
            Как правильно распределить бюджет?
          </h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Распределяйте бюджет пропорционально эффективности каналов. Начинайте с небольших бюджетов, 
            тестируйте и постепенно увеличивайте инвестиции в наиболее результативные каналы.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-500" />
          Стратегии повышения эффективности
        </h3>
        <ul>
          <li>Четко определяйте цели кампании</li>
          <li>Создавайте персонализированный контент</li>
          <li>Проводите A/B-тестирование креативов</li>
          <li>Непрерывно анализируйте и оптимизируйте кампанию</li>
          <li>Используйте ретаргетинг</li>
          <li>Адаптируйте рекламу под особенности каждой платформы</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            На что обратить внимание при планировании
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Точно определите целевую аудиторию</li>
            <li>Учитывайте сезонность и специфику бизнеса</li>
            <li>Контролируйте бюджет и не завышайте расходы</li>
            <li>Следите за изменениями алгоритмов рекламных платформ</li>
            <li>Будьте готовы к экспериментам и изменениям</li>
          </ul>
        </div>

        <h3>Как оценить эффективность кампании?</h3>
        <ol>
          <li>Сравните фактические показатели с целевыми</li>
          <li>Проанализируйте конверсию по каждому каналу</li>
          <li>Рассчитайте возврат инвестиций (ROI)</li>
          <li>Оцените качественные показатели (узнаваемость, engagement)</li>
          <li>Соберите обратную связь от клиентов</li>
        </ol>

        <p className="font-semibold mt-6">
          Наш калькулятор рекламных кампаний поможет вам спланировать, рассчитать и оптимизировать 
          рекламную стратегию, максимизировав эффективность ваших маркетинговых инвестиций!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;